/**
 * Cholesky Decomposition - Critical Mathematical Foundation
 * 
 * Implements numerically stable Cholesky decomposition for correlation matrices.
 * Essential for generating properly correlated random variables in Monte Carlo simulations.
 * 
 * Mathematical Foundation:
 * For a symmetric positive semi-definite matrix A, finds lower triangular matrix L such that:
 * A = L * L^T
 * 
 * This is THE correct method for generating correlated random variables.
 */

import { MathUtils } from '../simulation/utils/MathUtils';

export interface CholeskyResult {
  L: number[][];
  isValid: boolean;
  conditionNumber?: number;
  warnings: string[];
}

export interface MatrixValidation {
  isValid: boolean;
  isSymmetric: boolean;
  isPositiveSemiDefinite: boolean;
  hasUnitDiagonal: boolean;
  correlationsInRange: boolean;
  conditionNumber: number;
  errors: string[];
  warnings: string[];
}

export class CholeskyDecomposition {
  private static readonly EPSILON = 1e-10;
  private static readonly MIN_DIAGONAL = 1e-8;
  private static readonly MAX_CONDITION_NUMBER = 1e6;

  /**
   * Perform Cholesky decomposition with comprehensive validation
   */
  static decompose(matrix: number[][]): CholeskyResult {
    const validation = this.validateCorrelationMatrix(matrix);
    
    if (!validation.isValid) {
      return {
        L: this.regularizeAndDecompose(matrix),
        isValid: false,
        warnings: [
          ...validation.errors,
          'Matrix was regularized to ensure numerical stability'
        ]
      };
    }

    try {
      const L = this.performDecomposition(matrix);
      
      return {
        L,
        isValid: true,
        conditionNumber: validation.conditionNumber,
        warnings: validation.warnings
      };
    } catch (error) {
      // Fallback to regularized decomposition
      return {
        L: this.regularizeAndDecompose(matrix),
        isValid: false,
        warnings: [
          `Decomposition failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'Matrix was regularized to ensure numerical stability'
        ]
      };
    }
  }

  /**
   * Comprehensive validation of correlation matrix
   */
  static validateCorrelationMatrix(matrix: number[][]): MatrixValidation {
    const errors: string[] = [];
    const warnings: string[] = [];
    const n = matrix.length;

    // Check if matrix is square
    if (!matrix.every(row => row.length === n)) {
      errors.push('Matrix must be square');
      return {
        isValid: false,
        isSymmetric: false,
        isPositiveSemiDefinite: false,
        hasUnitDiagonal: false,
        correlationsInRange: false,
        conditionNumber: Infinity,
        errors,
        warnings
      };
    }

    // Check symmetry
    let isSymmetric = true;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (Math.abs(matrix[i][j] - matrix[j][i]) > this.EPSILON) {
          isSymmetric = false;
          errors.push(`Matrix not symmetric at [${i},${j}]: ${matrix[i][j]} != ${matrix[j][i]}`);
        }
      }
    }

    // Check diagonal elements (should be 1 for correlation matrix)
    let hasUnitDiagonal = true;
    for (let i = 0; i < n; i++) {
      if (Math.abs(matrix[i][i] - 1.0) > this.EPSILON) {
        hasUnitDiagonal = false;
        warnings.push(`Diagonal element [${i},${i}] is ${matrix[i][i]}, should be 1.0`);
      }
    }

    // Check correlation values are in [-1, 1]
    let correlationsInRange = true;
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i !== j && (matrix[i][j] < -1 - this.EPSILON || matrix[i][j] > 1 + this.EPSILON)) {
          correlationsInRange = false;
          errors.push(`Correlation [${i},${j}] = ${matrix[i][j]} out of range [-1, 1]`);
        }
      }
    }

    // Check positive semi-definiteness via determinant (simplified for small matrices)
    const isPositiveSemiDefinite = this.checkPositiveSemiDefinite(matrix);
    if (!isPositiveSemiDefinite) {
      errors.push('Matrix is not positive semi-definite');
    }

    // Calculate condition number
    const conditionNumber = this.estimateConditionNumber(matrix);
    if (conditionNumber > this.MAX_CONDITION_NUMBER) {
      warnings.push(`High condition number (${conditionNumber.toFixed(2)}). Matrix may be ill-conditioned.`);
    }

    return {
      isValid: errors.length === 0,
      isSymmetric,
      isPositiveSemiDefinite,
      hasUnitDiagonal,
      correlationsInRange,
      conditionNumber,
      errors,
      warnings
    };
  }

  /**
   * Perform the actual Cholesky decomposition
   * Algorithm: For each column j, compute L[i][j] using previously computed values
   */
  private static performDecomposition(matrix: number[][]): number[][] {
    const n = matrix.length;
    const L: number[][] = Array(n).fill(null).map(() => Array(n).fill(0));

    for (let j = 0; j < n; j++) {
      // Diagonal element
      let sum = 0;
      for (let k = 0; k < j; k++) {
        sum += L[j][k] * L[j][k];
      }
      
      const diagonalValue = matrix[j][j] - sum;
      
      if (diagonalValue < this.MIN_DIAGONAL) {
        throw new Error(
          `Negative or near-zero diagonal at position ${j}: ${diagonalValue}. ` +
          `Matrix may not be positive semi-definite.`
        );
      }
      
      L[j][j] = Math.sqrt(diagonalValue);

      // Off-diagonal elements
      for (let i = j + 1; i < n; i++) {
        let sum = 0;
        for (let k = 0; k < j; k++) {
          sum += L[i][k] * L[j][k];
        }
        L[i][j] = (matrix[i][j] - sum) / L[j][j];
      }
    }

    return L;
  }

  /**
   * Regularize matrix and decompose as fallback
   */
  private static regularizeAndDecompose(matrix: number[][]): number[][] {
    const n = matrix.length;
    const regularized: number[][] = Array(n).fill(null).map((_, i) => 
      Array(n).fill(null).map((_, j) => matrix[i][j])
    );

    // Ensure symmetry
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const avg = (regularized[i][j] + regularized[j][i]) / 2;
        regularized[i][j] = regularized[j][i] = avg;
      }
    }

    // Ensure unit diagonal
    for (let i = 0; i < n; i++) {
      regularized[i][i] = 1.0;
    }

    // Clamp correlations to [-0.99, 0.99] to avoid singularity
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i !== j) {
          regularized[i][j] = MathUtils.clamp(regularized[i][j], -0.99, 0.99);
        }
      }
    }

    // Add small ridge regularization to ensure positive definiteness
    const ridge = 0.001;
    for (let i = 0; i < n; i++) {
      regularized[i][i] += ridge;
    }

    // Attempt decomposition on regularized matrix
    try {
      return this.performDecomposition(regularized);
    } catch (error) {
      // Final fallback: identity matrix (uncorrelated)
      console.warn('Cholesky decomposition failed even after regularization. Using identity matrix.');
      return Array(n).fill(null).map((_, i) => 
        Array(n).fill(null).map((_, j) => i === j ? 1 : 0)
      );
    }
  }

  /**
   * Check if matrix is positive semi-definite
   * For small matrices (n <= 4), use determinant-based method
   * For larger matrices, attempt decomposition
   */
  private static checkPositiveSemiDefinite(matrix: number[][]): boolean {
    const n = matrix.length;
    
    if (n <= 4) {
      // Check all principal minors
      for (let k = 1; k <= n; k++) {
        const subMatrix = matrix.slice(0, k).map(row => row.slice(0, k));
        const det = this.calculateDeterminant(subMatrix);
        if (det < -this.EPSILON) {
          return false;
        }
      }
      return true;
    } else {
      // For larger matrices, try decomposition
      try {
        this.performDecomposition(matrix);
        return true;
      } catch {
        return false;
      }
    }
  }

  /**
   * Calculate determinant for small matrices
   */
  private static calculateDeterminant(matrix: number[][]): number {
    const n = matrix.length;
    
    if (n === 1) return matrix[0][0];
    if (n === 2) {
      return matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
    }
    if (n === 3) {
      return (
        matrix[0][0] * (matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]) -
        matrix[0][1] * (matrix[1][0] * matrix[2][2] - matrix[1][2] * matrix[2][0]) +
        matrix[0][2] * (matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0])
      );
    }
    
    // For n=4, use cofactor expansion (slow but accurate for small matrices)
    if (n === 4) {
      let det = 0;
      for (let j = 0; j < n; j++) {
        const subMatrix = matrix.slice(1).map(row => 
          row.filter((_, colIdx) => colIdx !== j)
        );
        det += (j % 2 === 0 ? 1 : -1) * matrix[0][j] * this.calculateDeterminant(subMatrix);
      }
      return det;
    }
    
    throw new Error('Determinant calculation not implemented for matrices larger than 4x4');
  }

  /**
   * Estimate condition number (simplified)
   * Condition number = ||A|| * ||A^-1||
   * High condition number indicates ill-conditioned matrix
   */
  private static estimateConditionNumber(matrix: number[][]): number {
    const n = matrix.length;
    
    // For correlation matrices, use Frobenius norm approximation
    let normA = 0;
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        normA += matrix[i][j] * matrix[i][j];
      }
    }
    normA = Math.sqrt(normA);
    
    // Estimate inverse norm using determinant
    const det = Math.abs(this.calculateDeterminant(matrix));
    
    if (det < this.EPSILON) {
      return Infinity;
    }
    
    // Rough approximation: condition number ≈ norm / sqrt(det)
    return normA / Math.sqrt(det);
  }

  /**
   * Generate correlated random variables using Cholesky decomposition
   */
  static generateCorrelatedVariables(
    L: number[][],
    independent: number[]
  ): number[] {
    const n = L.length;
    
    if (independent.length !== n) {
      throw new Error(`Independent variables length (${independent.length}) must match matrix dimension (${n})`);
    }

    const correlated = Array(n).fill(0);
    
    // Apply Cholesky transformation: Y = L * Z
    // where Z is independent standard normal, Y is correlated
    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        correlated[i] += L[i][j] * independent[j];
      }
    }

    return correlated;
  }

  /**
   * Verify that L * L^T = A (for testing)
   */
  static verifyDecomposition(L: number[][], original: number[][]): {
    isValid: boolean;
    maxError: number;
  } {
    const n = L.length;
    let maxError = 0;

    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        let reconstructed = 0;
        for (let k = 0; k < n; k++) {
          reconstructed += L[i][k] * L[j][k];
        }
        const error = Math.abs(reconstructed - original[i][j]);
        maxError = Math.max(maxError, error);
      }
    }

    return {
      isValid: maxError < 1e-6,
      maxError
    };
  }
}
